import React from 'react';
import { Container, Grid, Box, Typography, Card, Chip, Zoom } from '@mui/material';
import { motion } from 'framer-motion';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import CodeIcon from '@mui/icons-material/Code';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import DescriptionIcon from '@mui/icons-material/Description';
import { useScrollAnimation, fadeInUp, staggerContainer, scaleIn } from '../hooks/useScrollAnimation';

const ServicesSection = () => {
  const [titleRef, titleControls] = useScrollAnimation();
  const [servicesRef, servicesControls] = useScrollAnimation();
  const [techRef, techControls] = useScrollAnimation();
  const services = [
    {
      icon: <LanguageIcon />,
      title: 'Web Development',
      description:
        'Custom websites and web applications built with modern frameworks like React, Vue, Angular, and ASP.NET. Responsive, fast, and SEO-optimized solutions tailored to your business needs.',
    },
    {
      icon: <PhoneAndroidIcon />,
      title: 'Mobile Apps (iOS & Android)',
      description:
        'Native and cross-platform mobile applications using React Native and Flutter. Delivering seamless user experiences across all devices with beautiful, intuitive interfaces.',
    },
    {
      icon: <CodeIcon />,
      title: 'Desktop Applications',
      description:
        'Professional desktop application development with modern technologies. Very good experience in building high-performance, user-friendly desktop solutions for Windows and cross-platform environments.',
    },
    {
      icon: <CloudUploadIcon />,
      title: 'CI/CD & DevOps',
      description:
        'Implementing continuous integration and deployment pipelines. Docker configuration, Linux server administration, Kubernetes orchestration, GitHub Actions, and automated testing.',
    },
    {
      icon: <SettingsIcon />,
      title: 'PLM Systems & Enterprise',
      description:
        'Expert in Aras Corp and Teamcenter PLM systems. Building plugins, custom workflows, and complex deployment solutions. Experience as Product Manager with deep technical implementation knowledge.',
    },
    {
      icon: <IntegrationInstructionsIcon />,
      title: 'API Development & Integration',
      description:
        'RESTful and GraphQL API design and development. Microservices architecture, third-party integrations, and robust backend systems. Expertise in complex system integrations.',
    },
    {
      icon: <DescriptionIcon />,
      title: 'Document Processing Services',
      description:
        'Specialized in XRechnung and ZUGFeRD invoice processing services. Building compliant, automated document handling systems for enterprise environments.',
    },
    {
      icon: <SecurityIcon />,
      title: 'CMS & WordPress Solutions',
      description:
        'WordPress and Joomla development, custom plugin creation, and theme development. Building secure, scalable content management solutions with custom functionality.',
    },
  ];

  const techStack = [
    'C#',
    'Java',
    'JavaScript',
    'TypeScript',
    'Python',
    'React',
    'Node.js',
    'ASP.NET',
    'Spring Boot',
    'React Native',
    'Flutter',
    'Docker',
    'Kubernetes',
    'Linux',
    'GitHub Actions',
    'Jenkins',
    'AWS',
    'Azure',
    'PostgreSQL',
    'MongoDB',
    'Aras PLM',
    'Teamcenter',
    'WordPress',
    'Joomla',
  ];

  return (
    <Box id="services" sx={{ py: { xs: 10, md: 15 } }}>
      <Container maxWidth="xl">
        <Box 
          component={motion.div}
          ref={titleRef}
          initial="hidden"
          animate={titleControls}
          variants={fadeInUp}
          sx={{ mb: 8, textAlign: 'center' }}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.5rem', md: '4rem' },
              mb: 2,
            }}
          >
            Services & Expertise
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
              maxWidth: '700px',
              mx: 'auto',
            }}
          >
            Comprehensive development solutions from concept to deployment
          </Typography>
        </Box>

        <Grid 
          container 
          spacing={4}
          component={motion.div}
          ref={servicesRef}
          initial="hidden"
          animate={servicesControls}
          variants={staggerContainer}
        >
          {services.map((service, idx) => (
            <Grid item xs={12} sm={6} lg={4} key={idx}>
              <Zoom in timeout={500 + idx * 100}>
                <Card
                  component={motion.div}
                  variants={scaleIn}
                  whileHover={{ 
                    y: -12, 
                    transition: { duration: 0.3 } 
                  }}
                  sx={{
                    height: '100%',
                    background: 'var(--card-bg)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid var(--border-subtle)',
                    p: 4,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(135deg, rgba(0, 245, 255, 0.1) 0%, transparent 100%)',
                      opacity: 0,
                      transition: 'opacity 0.4s ease',
                    },
                    '&:hover': {
                      transform: 'translateY(-12px)',
                      borderColor: 'primary.main',
                      boxShadow: '0 20px 60px rgba(0, 245, 255, 0.25)',
                      '&::before': {
                        opacity: 1,
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #00F5FF, #FF6B35)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      position: 'relative',
                      zIndex: 1,
                      '& svg': {
                        fontSize: 36,
                        color: '#0B0D17',
                      },
                    }}
                  >
                    {service.icon}
                  </Box>

                  <Typography
                    variant="h5"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    {service.title}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.8,
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    {service.description}
                  </Typography>
                </Card>
              </Zoom>
            </Grid>
          ))}
        </Grid>

        {/* Tech Stack */}
        <Box 
          component={motion.div}
          ref={techRef}
          initial="hidden"
          animate={techControls}
          variants={fadeInUp}
          sx={{ mt: 10, textAlign: 'center' }}
        >
          <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
            Technology Stack
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            {techStack.map((tech, idx) => (
              <Chip
                key={idx}
                label={tech}
                sx={{
                  px: 2,
                  py: 3,
                  fontSize: '0.9rem',
                  fontFamily: "'Fira Code', monospace",
                  fontWeight: 500,
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: 'text.primary',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(0, 245, 255, 0.1)',
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    transform: 'translateY(-3px)',
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ServicesSection;
